import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, ScrollText, Dices, Sparkles, Plus, ChevronRight, Swords } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageLoader, EmptyState } from "@/components/common";
import { mod, fmtMod } from "@/lib/dnd";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard")).data,
  });

  if (isLoading) return <PageLoader />;
  const stats = data?.stats || {};
  const cards = [
    { label: "Characters", value: stats.characters || 0, icon: Users, color: "from-violet-500 to-violet-800", to: "/characters" },
    { label: "Campaigns", value: stats.campaigns || 0, icon: ScrollText, color: "from-amber-500 to-orange-700", to: "/campaigns" },
    { label: "Dice Rolls", value: stats.rolls || 0, icon: Dices, color: "from-teal-500 to-cyan-700", to: "/dice" },
  ];

  return (
    <div>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <p className="text-sm text-violet-300/80 font-medium">Welcome back</p>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mt-1">
          {user?.username || "Adventurer"}
        </h1>
        <p className="text-zinc-500 mt-2">Ready your dice — adventure awaits beyond the table.</p>
      </motion.div>

      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        {cards.map((c, i) => (
          <motion.button
            key={c.label}
            onClick={() => navigate(c.to)}
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}
            className="glass glass-hover rounded-3xl p-5 text-left relative overflow-hidden group"
            data-testid={`stat-${c.label.toLowerCase()}`}
          >
            <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${c.color} flex items-center justify-center mb-4`}>
              <c.icon size={22} className="text-white" />
            </div>
            <div className="font-display text-4xl font-bold text-white">{c.value}</div>
            <div className="text-sm text-zinc-400 mt-1 flex items-center gap-1">
              {c.label}
              <ChevronRight size={14} className="opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition" />
            </div>
          </motion.button>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl text-white">Recent Heroes</h2>
            <Link to="/characters" className="text-sm text-violet-300 hover:text-violet-200 flex items-center gap-1">
              View all <ChevronRight size={15} />
            </Link>
          </div>
          {data?.recent_characters?.length ? (
            <div className="grid sm:grid-cols-2 gap-3">
              {data.recent_characters.map((c) => (
                <Link
                  key={c.id}
                  to={`/characters/${c.id}`}
                  data-testid={`recent-char-${c.id}`}
                  className="glass glass-hover rounded-2xl p-4 flex items-center gap-4"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600 to-violet-900 flex items-center justify-center font-display font-bold">
                    {c.name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-white truncate">{c.name}</div>
                    <div className="text-xs text-zinc-500 truncate">
                      Lvl {c.level} {c.race} {c.class}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-zinc-500">AC</div>
                    <div className="font-display font-bold text-violet-300">{c.armor_class}</div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <EmptyState
              icon={Swords}
              title="No heroes yet"
              subtitle="Forge your first character and begin the legend."
              action={
                <button onClick={() => navigate("/characters")} className="btn-violet rounded-xl px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2">
                  <Plus size={16} /> Create Character
                </button>
              }
            />
          )}
        </div>

        <div>
          <h2 className="font-display text-xl text-white mb-4">Recent Rolls</h2>
          <div className="glass rounded-3xl p-4 space-y-2.5 min-h-[120px]">
            {data?.recent_rolls?.length ? (
              data.recent_rolls.map((r) => (
                <div key={r.id} className="flex items-center justify-between py-1.5 border-b border-white/[0.05] last:border-0">
                  <div>
                    <span className="font-mono text-sm text-zinc-300">{r.notation}</span>
                    {r.label && <span className="text-xs text-zinc-600 ml-2">{r.label}</span>}
                  </div>
                  <span className="font-display font-bold text-lg text-violet-300">{r.total}</span>
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center h-full py-8 text-center">
                <Dices size={28} className="text-zinc-700 mb-2" />
                <p className="text-sm text-zinc-600">Roll some dice to see history</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
